/**
 * Types for the boards demo data, ported from the design mockup's
 * `flowboard-store.js`. Frontend-only; no backend model exists yet.
 *
 * Every entity `id` is a UUID string: deterministic in seed data
 * (board_seed's uuidifySeed) and random at runtime.
 */

export enum CardStatus {
   TODO = 'todo',
   IN_PROGRESS = 'doing',
   DONE = 'done',
}

export enum CardPriority {
   LOW = 'low',
   MEDIUM = 'medium',
   HIGH = 'high',
   CRITICAL = 'critical',
}

export enum OrgRole {
   OWNER = 'owner',
   ADMIN = 'admin',
   MEMBER = 'member',
   GUEST = 'guest',
}

export enum MemberStatus {
   ACTIVE = 'active',
   PENDING = 'pending',
   SUSPENDED = 'suspended',
}

export enum SeatType {
   FULL = 'full',
   LIGHT = 'light',
   VIEWER = 'viewer',
}

export enum BoardAccess {
   EDIT = 'edit',
   COMMENT = 'comment',
   VIEW = 'view',
}

export enum BoardVisibility {
   PRIVATE = 'private',
   WORKSPACE = 'workspace',
   ORGANIZATION = 'organization',
   PUBLIC = 'public',
}

export enum CustomFieldType {
   TEXT = 'text',
   NUMBER = 'number',
   DROPDOWN = 'dropdown',
   DATE = 'date',
   CHECKBOX = 'checkbox',
   MEMBER = 'member',
   PROGRESS = 'progress',
}

export enum InvitationStatus {
   PENDING = 'pending',
   ACCEPTED = 'accepted',
   REJECTED = 'rejected',
   CANCELED = 'canceled',
   EXPIRED = 'expired',
}

export enum ApiKeyOwnerType {
   USER = 'user',
   ORGANIZATION = 'organization',
}

export enum CardSortBy {
   MANUAL = 'manual',
   PRIORITY = 'priority',
   DUE = 'due',
   TITLE = 'title',
}

export enum CardGroupBy {
   STATUS = 'status',
   PRIORITY = 'priority',
   ASSIGNEE = 'assignee',
}

/**
 * A demo member: a user and their membership in one record. `*Label` fields
 * are display strings ("2 days ago"), not timestamps.
 */
export interface IMember {
   id: string;
   name: string;
   email: string;
   /** Whether the email address has been verified. */
   emailVerified: boolean;
   /** Normalized unique handle (lowercase). */
   username: string;
   /** Username with the owner's preferred casing (display only). */
   displayUsername: string;
   initials: string;
   /** Decorative avatar color (hex). */
   color: string;
   orgRole: OrgRole;
   status: MemberStatus;
   /** App-level administrator (grants access to the /admin area). */
   isAppAdmin: boolean;
   /** Banned by an app admin (blocks sign-in). */
   isBanned: boolean;
   banReason: string | null;
   /** Human-readable ban expiry ("in 30 days"); null = permanent or not banned. */
   banExpiresLabel: string | null;
   title: string;
   department: string;
   seat: SeatType;
   twoFactorEnabled: boolean;
   joinedLabel: string;
   lastActiveLabel: string;
}

/** An invitation to join an organization, addressed to an email. */
export interface IInvitation {
   id: string;
   organizationId: string;
   email: string;
   role: OrgRole;
   status: InvitationStatus;
   /** Display name of the inviting member. */
   inviterName: string;
   inviterEmail: string;
   sentLabel: string;
   expiresLabel: string;
}

/** An API key owned by a user or an organization. Only `start` is ever shown again. */
export interface IApiKey {
   id: string;
   ownerType: ApiKeyOwnerType;
   /** Member id or organization id, per ownerType. */
   ownerId: string;
   name: string;
   /** Identifying prefix of the secret ("ab_live_3f9x…"). */
   start: string;
   createdLabel: string;
   /** null = never expires. */
   expiresLabel: string | null;
   enabled: boolean;
   lastRequestLabel: string | null;
   requestCount: number;
}

export interface ILabel {
   id: string;
   name: string;
   /** Decorative label color (hex). */
   color: string;
}

export interface ICustomFieldDef {
   id: string;
   name: string;
   type: CustomFieldType;
   /** Choices for DROPDOWN fields. */
   options?: readonly string[];
}

export interface IChecklistItem {
   text: string;
   isDone: boolean;
}

export interface ICardComment {
   memberId: string;
   text: string;
   whenLabel: string;
}

export interface ICardActivity {
   memberId: string;
   action: string;
   whenLabel: string;
}

export interface ICard {
   id: string;
   title: string;
   status: CardStatus;
   priority: CardPriority;
   assigneeId: string | null;
   /** ISO date (YYYY-MM-DD) or null when no due date is set. */
   dueDate: string | null;
   description: string;
   labelIds: string[];
   /** Values keyed by ICustomFieldDef id. */
   fieldValues: Record<string, string | number | boolean>;
   /** ISO date (YYYY-MM-DD) or null. */
   createdDate: string | null;
   watcherIds: string[];
   checklist: IChecklistItem[];
   comments: ICardComment[];
   activity: ICardActivity[];
}

export interface IBoardShareEntry {
   memberId: string;
   access: BoardAccess;
}

export interface IBoard {
   id: string;
   /** Organization (from the real session) this board belongs to; unset
    * until ensureWorkspaceContext() assigns the seed boards. */
   organizationId?: string | null;
   name: string;
   /** Decorative board swatch color (hex). */
   color: string;
   isStarred: boolean;
   description: string;
   visibility: BoardVisibility;
   memberIds: string[];
   labels: ILabel[];
   fields: ICustomFieldDef[];
   cards: ICard[];
   share: IBoardShareEntry[];
}

export enum FolderNodeType {
   FOLDER = 'folder',
   BOARD = 'board',
}

/**
 * Node in the sidebar folder tree. FOLDER nodes have `name` + `children`;
 * BOARD nodes reference a board by `id`.
 */
export interface IFolderTreeNode {
   type: FolderNodeType;
   id: string;
   name?: string;
   children?: IFolderTreeNode[];
}

export interface IOrgSecurity {
   ssoEnabled: boolean;
   enforceTwoFactor: boolean;
   guestLinksEnabled: boolean;
   publicBoardsEnabled: boolean;
   domainCaptureEnabled: boolean;
}

export interface IOrganization {
   id: string;
   name: string;
   /** URL-safe unique identifier ("acme-inc"). */
   slug: string;
   plan: string;
   domain: string;
   billingEmail: string;
   seatsTotal: number;
   defaultRole: OrgRole;
   /** Decorative org avatar color (hex). */
   color: string;
   security: IOrgSecurity;
}

export interface INotification {
   memberId: string;
   text: string;
   whenLabel: string;
   boardName: string;
   isRead: boolean;
}

/** Per-session view state for the current board (search/filter/sort/group). */
export interface ICardViewState {
   query: string;
   filterPriority: CardPriority | null;
   filterLabelId: string | null;
   filterAssigneeId: string | null;
   sortBy: CardSortBy;
   groupBy: CardGroupBy;
}

// ---------------------------------------------------------------------------
// Reference data (static lookups for settings pages)
// ---------------------------------------------------------------------------

export interface IRoleDef {
   role: OrgRole;
   label: string;
   description: string;
   permissions: readonly string[];
}

export const ROLE_DEFS: readonly IRoleDef[] = [
   {
      role: OrgRole.OWNER,
      label: 'Owner',
      description: 'Full control including billing and deleting the organization.',
      permissions: [
         'Manage billing & plan',
         'Add or remove admins',
         'Delete organization',
         'All admin abilities',
      ],
   },
   {
      role: OrgRole.ADMIN,
      label: 'Admin',
      description: 'Manage members, boards, and security settings.',
      permissions: [
         'Invite & remove members',
         'Change member roles',
         'Manage all boards',
         'Edit security settings',
      ],
   },
   {
      role: OrgRole.MEMBER,
      label: 'Member',
      description: 'Create and collaborate on boards they belong to.',
      permissions: ['Create boards', 'Edit assigned boards', 'Invite guests to own boards'],
   },
   {
      role: OrgRole.GUEST,
      label: 'Guest',
      description: 'Limited access to specific boards they are invited to.',
      permissions: ['View & comment on invited boards', 'No access to org settings'],
   },
];

export interface ISeatTypeDef {
   seat: SeatType;
   label: string;
   description: string;
}

export const SEAT_TYPE_DEFS: readonly ISeatTypeDef[] = [
   {seat: SeatType.FULL, label: 'Full', description: 'Create & edit across all boards'},
   {seat: SeatType.LIGHT, label: 'Light', description: 'Comment & limited editing'},
   {seat: SeatType.VIEWER, label: 'Viewer', description: 'Read-only access'},
];

export const DEPARTMENTS: readonly string[] = [
   'Product',
   'Engineering',
   'Design',
   'Marketing',
   'Operations',
   'Leadership',
];
